import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

interface Template {
  build(rand: () => number, d: Difficulty): { prompt: string; correct: number; explanation: string };
}

// Template 1: buy items then get change
// 小明買了 X 顆蘋果，每顆 Y 元，付了 Z 元，找回多少？  Z - X*Y
const t1: Template = {
  build(rand, d) {
    const X = d === 1 ? randInt(rand, 2, 5) : d === 2 ? randInt(rand, 3, 9) : randInt(rand, 5, 12);
    const Y = d === 1 ? randInt(rand, 5, 15) : d === 2 ? randInt(rand, 8, 25) : randInt(rand, 10, 30);
    const cost = X * Y;
    // Make Z a round number larger than cost
    const Z = cost + randInt(rand, 1, 5) * 10;
    const correct = Z - cost;
    return {
      prompt: `小明買了 ${X} 顆蘋果，每顆 ${Y} 元，付了 ${Z} 元，找回多少元？`,
      correct,
      explanation: `共花 ${X}×${Y}=${cost} 元，${Z}-${cost}=${correct} 元`,
    };
  },
};

// Template 2: distribute equally then count remaining
// 有 X 顆糖，每 Y 顆裝一袋，裝了幾袋，剩幾顆？  floor(X/Y) bags, X%Y left
// Reformulated as MCQ: 共有幾袋（取商）
const t2: Template = {
  build(rand, d) {
    const Y = d === 1 ? randInt(rand, 3, 5) : randInt(rand, 4, 8);
    const bags = d === 1 ? randInt(rand, 3, 7) : randInt(rand, 4, 12);
    const leftover = randInt(rand, 1, Y - 1);
    const X = bags * Y + leftover;
    const correct = bags;
    return {
      prompt: `有 ${X} 顆糖，每 ${Y} 顆裝一袋，可以裝幾袋？`,
      correct,
      explanation: `${X}÷${Y}=${bags} 袋餘 ${leftover} 顆，答案是 ${bags} 袋`,
    };
  },
};

// Template 3: two groups each buy, total cost
// 甲班有 X 人，乙班有 Y 人，每人 Z 元，共花多少？  (X+Y)*Z
const t3: Template = {
  build(rand, d) {
    const X = d === 1 ? randInt(rand, 5, 15) : randInt(rand, 8, 20);
    const Y = d === 1 ? randInt(rand, 3, 10) : randInt(rand, 5, 15);
    const Z = d === 1 ? randInt(rand, 5, 15) : d === 2 ? randInt(rand, 8, 20) : randInt(rand, 10, 30);
    const correct = (X + Y) * Z;
    return {
      prompt: `甲組有 ${X} 人，乙組有 ${Y} 人，每人要付 ${Z} 元，兩組共花多少元？`,
      correct,
      explanation: `共 ${X}+${Y}=${X+Y} 人，${X+Y}×${Z}=${correct} 元`,
    };
  },
};

// Template 4: earn money over days, subtract expense
// 小明每天存 X 元，存了 Y 天，買了一本書花 Z 元，還剩多少？  X*Y - Z
const t4: Template = {
  build(rand, d) {
    const X = d === 1 ? randInt(rand, 5, 15) : d === 2 ? randInt(rand, 10, 25) : randInt(rand, 15, 40);
    const Y = d === 1 ? randInt(rand, 5, 10) : randInt(rand, 7, 15);
    const total = X * Y;
    const Z = randInt(rand, 1, Math.max(1, Math.floor(total * 0.7)));
    const correct = total - Z;
    return {
      prompt: `小明每天存 ${X} 元，存了 ${Y} 天，花了 ${Z} 元買書，還剩幾元？`,
      correct,
      explanation: `存了 ${X}×${Y}=${total} 元，${total}-${Z}=${correct} 元`,
    };
  },
};

// Template 5: rows × cols table, subtract a section
// 停車場有 X 排，每排 Y 個車位，已停了 Z 輛，還剩多少？  X*Y - Z
const t5: Template = {
  build(rand, d) {
    const X = d === 1 ? randInt(rand, 3, 6) : d === 2 ? randInt(rand, 4, 8) : randInt(rand, 5, 12);
    const Y = d === 1 ? randInt(rand, 5, 10) : d === 2 ? randInt(rand, 8, 15) : randInt(rand, 10, 20);
    const total = X * Y;
    const Z = randInt(rand, 1, Math.max(1, Math.floor(total * 0.6)));
    const correct = total - Z;
    return {
      prompt: `停車場有 ${X} 排，每排 ${Y} 個車位，已停了 ${Z} 輛，還剩幾個空位？`,
      correct,
      explanation: `共 ${X}×${Y}=${total} 個車位，${total}-${Z}=${correct} 個空位`,
    };
  },
};

const TEMPLATES: Template[] = [t1, t2, t3, t4, t5];

export function generateTwoStep(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  // Pick template deterministically based on seed
  const tIdx = Math.floor(rand() * TEMPLATES.length);
  const template = TEMPLATES[tIdx];

  const { prompt, correct, explanation } = template.build(rand, difficulty);

  // Build 3 plausible mistake distractors
  const distractors = new Set<number>();

  // Common two-step mistakes: only doing one of the two operations
  const offset1 = Math.floor(rand() * 20) + 5;
  const offset2 = Math.floor(rand() * 30) + 10;
  const offset3 = Math.floor(rand() * 50) + 20;

  const candidates = [
    correct + offset1,
    correct - offset1,
    correct + offset2,
    correct - offset2,
    correct + offset3,
    correct - offset3,
  ].filter(c => c > 0 && c !== correct);

  for (const c of candidates) {
    if (distractors.size >= 3) break;
    distractors.add(c);
  }

  // Fallback if not enough
  let extra = 1;
  while (distractors.size < 3) {
    const candidate = correct + extra * 7;
    if (candidate > 0 && candidate !== correct && !distractors.has(candidate)) {
      distractors.add(candidate);
    }
    extra++;
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  // Deterministic shuffle via seed
  const shuffled = [...optionList].sort((x, y) => {
    const px = mulberry32(seed + x)();
    const py = mulberry32(seed + y)();
    return px - py;
  });

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.word.two-step',
  };
}
